import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { MessageService, ConfirmationService } from 'primeng/api';
import { SupervisorService } from 'src/app/services/supervisor.service';
import { Enfermera } from 'src/app/models/enfermera.interface';
import { Paginar } from 'src/app/models/http-paginacion.interface';
import { CasoClinico, CasoClinicoFormatoXLS } from 'src/app/models/caso-clinico.interface';
import { Medico } from 'src/app/models/medico.interface';
import { CANJE } from 'src/app/shared/constantes/constants';
import { RedimirEnfermera } from 'src/app/models/redimir.interface';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-supervisor-enfermera',
  templateUrl: './supervisor-enfermera.component.html',
  styleUrls: ['./supervisor-enfermera.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class SupervisorEnfermeraComponent {
  idEnfermera: string = '0';
  enfermera!: Enfermera;
  medicoForm!: FormGroup;
  catalogoMedicos: Medico[] = [];
  statusCanje: string[] = [...CANJE];
  currentPageCasosClinicos: Paginar = { page: 0, first: 0, rows: 10, pageCount: 0 };
  totalItemsCasosClinicos: number = 10;

  currentPageMedicos: Paginar = { page: 0, first: 0, rows: 10, pageCount: 0 };
  totalItemsMedicos: number = 10;

  currentPageCanjes: Paginar = { page: 0, first: 0, rows: 10, pageCount: 0 };
  totalItemsCanjes: number = 10;

  productividadEnfermeraForm!: FormGroup;

  get f() {
    return this.productividadEnfermeraForm.controls;
  }
  get casosClinicos(): CasoClinico[] {
    const startIndex = (this.currentPageCasosClinicos.page) * this.currentPageCasosClinicos.rows;
    return this.enfermera.casosclinicos.slice(startIndex, startIndex + this.currentPageCasosClinicos.rows);
  }

  get totalPaginasCasosClinicos(): number {
    return Math.ceil(this.enfermera.casosclinicos.length / this.currentPageCasosClinicos.rows);
  }

  get medicos(): Medico[] {
    const startIndex = (this.currentPageMedicos.page) * this.currentPageMedicos.rows;
    return this.enfermera.medicos.slice(startIndex, startIndex + this.currentPageMedicos.rows);
  }

  get totalPaginasMedicos(): number {
    return Math.ceil(this.enfermera.medicos.length / this.currentPageMedicos.rows);
  }

  get canjes(): RedimirEnfermera[] {
    const startIndex = (this.currentPageCanjes.page) * this.currentPageCanjes.rows;
    return this.enfermera.canjes.slice(startIndex, startIndex + this.currentPageCanjes.rows);
  }

  get totalPaginasCanjes(): number {
    return Math.ceil(this.enfermera.canjes.length / this.currentPageCanjes.rows);
  }
  get canjesPagados(): number {
    return this.enfermera.canjes.filter((item) => item.status == 3).reduce((acumulador, item) => acumulador + item.redimir, 0)
  }
  constructor(
    private router: Router,
    private fb: FormBuilder,
    private activedRoute: ActivatedRoute,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private supervisorService: SupervisorService) {
    this.idEnfermera = this.activedRoute.snapshot.params['id'];
    this.medicoForm = this.crearFormulario();
    this.obtenerInformacion(this.idEnfermera);
    this.productividadEnfermeraForm = this.crearFormularioProductividad();
  }

  obtenerInformacion(id: string): void {
    this.cargarDatos(id);
  }

  crearFormularioProductividad(): FormGroup {
    return this.fb.group({
      fechaInicio: ['', Validators.required],
      fechaFin: ['', Validators.required]
    })
  }

  crearFormulario(): FormGroup {
    return this.fb.group({
      medico: ['', Validators.required]
    });
  }

  onSubmit(): void {
    const medico: Medico = <Medico>this.medicoForm.get('medico')?.value;
    const fk_medico: number = medico.id;
    const fk_enfermera: number = this.enfermera.id;

    this.supervisorService
      .asociarEnfermeraMedico(fk_enfermera, fk_medico)
      .subscribe({
        next: (resp) => {
          this.enfermera.medicos.push(resp.datos);
          this.totalItemsMedicos = this.enfermera.medicos.length;
          this.showSuccess(resp.mensaje);
          this.resetForm();
        },
        error: (err: HttpErrorResponse) => { this.showError(err.error.message) }
      })
    console.log(this.medicoForm.value);
  }

  cargarDatos(id: string): void {
    forkJoin({
      subEnfermera: this.supervisorService.getEnfermera(id),
      subCatalogo: this.supervisorService.getCatalogoMedicos(),
    }).subscribe({
      next: (resp) => {
        this.fullEnfermera(resp.subEnfermera.datos);
        this.fullCatalogo(resp.subCatalogo.datos);
      },
      error: (err) => { console.log(err) }
    });
  }

  fullEnfermera(enfermera: Enfermera): void {
    this.enfermera = enfermera
    this.asignarTotales(enfermera);
  }

  asignarTotales(enfermera: Enfermera): void {
    this.totalItemsCasosClinicos = enfermera.casosclinicos.length;
    this.totalItemsMedicos = enfermera.medicos.length;
    this.totalItemsCanjes = enfermera.canjes.length;
  }

  fullCatalogo(medicos: Medico[]): void {
    this.catalogoMedicos.push(...medicos);
  }

  eliminarMedico(idMedico: number): void {
    this.confirmationService.confirm({
      message: 'Estas seguro de quitar al medico?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        let idEnfermera = this.enfermera.id;
        this.supervisorService.desasociarEnfermeraMedico(idEnfermera, idMedico)
          .subscribe({
            next: (resp) => {
              this.enfermera.medicos = resp.datos.medicos;
              this.showSuccess(resp.mensaje);
            },
            error: (err: HttpErrorResponse) => { this.showError(err.error.message); console.log(err); }
          })
      }
    });

  }

  onPageChangeCasosClinicos(event: any) {
    this.currentPageCasosClinicos = <Paginar>event;
  }

  onPageChangeMedicos(event: any) {
    this.currentPageMedicos = <Paginar>event;
  }

  onPageChangeCanjes(event: any) {
    this.currentPageCanjes = <Paginar>event;
  }

  showSuccess(mensaje: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: mensaje
    });
  }

  showError(mensaje: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mensaje
    });
  }

  getCanje(id: number): string {
    return this.statusCanje[id];
  }

  formatDateISO(date: Date) {
    // Convert the date to ISO string
    const isoString = date.toISOString();
    // Split at the "T" character to get the date part
    const formattedDate = isoString.split("T")[0];
    return formattedDate;
  }

  exportPDF(): void {
    const fechas = this.productividadEnfermeraForm.value;
    const fechaInicio = this.formatDateISO(fechas.fechaInicio);
    const fechaFin = this.formatDateISO(fechas.fechaFin);
    const url = environment.urlbase + environment.pdfs.productividadEnfermera + this.idEnfermera + `/${fechaInicio}/${fechaFin}`;
    window.open(url, '_blank');
  }

  exportExcel(): void {
    const EXCEL_EXTENSION = '.xlsx';
    const casosClinicos: CasoClinicoFormatoXLS[] = this.fullCasosClinicosXLSX(this.enfermera.casosclinicos);
    const nombreEnfermera = [{ campo: "Enfermera", nomEnfermera: this.enfermera.nom_enfermera }];
    const fileName = 'enfermera-casosclinicos';

    import('xlsx').then((XLSX) => {
      const ws = XLSX.utils.json_to_sheet([]);
      XLSX.utils.sheet_add_json(ws, nombreEnfermera, { origin: "A2", skipHeader: true });
      XLSX.utils.sheet_add_json(ws, casosClinicos, { origin: "A4" });
      /* generate workbook and add the worksheet */
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'casos_clinicos');
      /* save to file */

      XLSX.writeFile(wb, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
    });
  }

  fullCasosClinicosXLSX(casoclinico: CasoClinico[]): CasoClinicoFormatoXLS[] {
    const casosclinicosxlsx: CasoClinicoFormatoXLS[] = casoclinico.map((item) => {
      return <CasoClinicoFormatoXLS>{
        fecha: item.fecha,
        folio: item.folio,
        serie: item.serie,
        paciente: item.paciente,
        sexo: item.sexo,
        edad: item.edad,
        antecedente: item.antecedente,
        institucion: item.cveInstitucion,
        hospital: item.nomHospital,
        fechaAplicacionProducto: item.fechaAplicacionProducto,
        diagnostico: item.diagnostico,
        antibiotico1: item.nomAntibiotico1,
        cantidad1: item.cantidad1,
        antibiotico2: item.nomAntibiotico2,
        cantidad2: item.cantidad2,
        antibiotico3: item.nomAntibiotico3,
        cantidad3: item.cantidad3,
        tratamiento: item.tratamiento,
        cuerpo: item.nomCuerpo,
        observacion: item.observacion,
        status: this.statusCanje[item.status_canje],
        medico: item.medico.nom_medico,
        productos: item.productos.length,
        documentos: item.documentos.length
      }
    });
    return casosclinicosxlsx;
  }

  resetForm(): void {
    this.medicoForm.reset();
  }
}